import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PlusCircle, Save, Zap, BatteryCharging, RotateCcw, MoreHorizontal } from "lucide-react";

function NumericInput({ value, onChange, ...props }) {
  const handleChange = (e) => { onChange(e.target.value.replace(",", ".")); };
  return <Input type="text" inputMode="decimal" enterKeyHint="next" value={value !== null && value !== undefined ? String(value) : ""} onChange={handleChange} {...props} />;
}

const CELL_COUNTS = [20, 28, 30, 40];

export default function CreateCellsPage() {
  const navigate = useNavigate();
  const [count, setCount] = useState(null);
  const [prefix, setPrefix] = useState("");
  const [cells, setCells] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isCustomMode, setIsCustomMode] = useState(false);

  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customCount, setCustomCount] = useState("");

  const generateCells = () => {
    if (!prefix.trim() || !count) return;
    setIsCustomMode(false);
    const p = prefix.trim().toUpperCase();
    const newCells = Array.from({ length: count }, (_, i) => ({
      cell_number: `${p}${i + 1}`,
      series: p,
      capacity_ah: null,
      internal_resistance: null,
      discharge_time: null,
      cycles: cycles.map((c) => ({ ...c, voltage_start: null, voltage_end: null })),
      state: "Bon",
    }));
    setCells(newCells);
  };

  const generateCustomCells = () => {
    const n = parseInt(customCount, 10);
    if (!n || n < 1 || n > 500) { toast.error("Nombre entre 1 et 500"); return; }
    setIsCustomMode(true);
    setCount(n);
    setShowCustomModal(false);
    const newCells = Array.from({ length: n }, () => ({
      cell_number: "",
      series: "",
      capacity_ah: null,
      internal_resistance: null,
      discharge_time: null,
      cycles: cycles.map((c) => ({ ...c, voltage_start: null, voltage_end: null })),
      state: "Bon",
    }));
    setCells(newCells);
    setCustomCount("");
  };

  const addCycle = (type) => {
    const existing = cycles.filter((c) => c.type === type).length;
    const newCycle = { type, index: existing + 1 };
    const updatedCycles = [...cycles, newCycle];
    setCycles(updatedCycles);
    setCells((prev) =>
      prev.map((cell) => ({
        ...cell,
        cycles: [...(cell.cycles || []), { ...newCycle, voltage_start: null, voltage_end: null }],
      }))
    );
  };

  const updateCell = (rowIdx, field, value) => {
    setCells((prev) => {
      const updated = [...prev];
      updated[rowIdx] = { ...updated[rowIdx], [field]: value };
      return updated;
    });
  };

  const updateCycleValue = (rowIdx, cycleIdx, field, value) => {
    setCells((prev) => {
      const updated = [...prev];
      const cellCycles = [...(updated[rowIdx].cycles || [])];
      cellCycles[cycleIdx] = { ...cellCycles[cycleIdx], [field]: value };
      updated[rowIdx] = { ...updated[rowIdx], cycles: cellCycles };
      return updated;
    });
  };

  const saveBatch = async () => {
    if (cells.length === 0) return;
    const empty = cells.some((c) => !c.cell_number?.trim());
    if (empty) { toast.error("Chaque cellule doit avoir un numero"); return; }
    const series = prefix.trim().toUpperCase() || cells[0].cell_number.replace(/[0-9]+$/, "").toUpperCase() || "CUSTOM";
    setSaving(true);
    try {
      const payload = {
        series,
        count: cells.length,
        cells: cells.map((c) => ({
          ...c,
          cell_number: c.cell_number.trim(),
          series: c.series || series,
          capacity_ah: c.capacity_ah ? parseFloat(String(c.capacity_ah).replace(",", ".")) : null,
          internal_resistance: c.internal_resistance ? parseFloat(String(c.internal_resistance).replace(",", ".")) : null,
          discharge_time: c.discharge_time ? parseFloat(String(c.discharge_time).replace(",", ".")) : null,
          cycles: (c.cycles || []).map((cy) => ({
            ...cy,
            voltage_start: cy.voltage_start ? parseFloat(String(cy.voltage_start).replace(",", ".")) : null,
            voltage_end: cy.voltage_end ? parseFloat(String(cy.voltage_end).replace(",", ".")) : null,
          })),
        })),
      };
      const { data } = await api.post(`/api/cells/batch`, payload);
      toast.success(`Lot de ${cells.length} cellules cree avec succes`);
      if (data?.without_measures > 0) {
        toast.warning(`${data.without_measures} cellule(s) sans capacite ou IR : elles n'apparaitront pas dans Generate Bloc tant que leurs mesures ne sont pas renseignees.`, { duration: 12000 });
      }
      navigate("/library");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la creation du lot");
    } finally {
      setSaving(false);
    }
  };

  const cycleLabel = (c) => (c.type === "decharge" ? `D${c.index}` : `R${c.index}`);

  const handleNextFocus = (e, rowIdx, colName) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const nextRow = rowIdx + 1;
      if (nextRow < cells.length) {
        const nextInput = document.querySelector(`[data-row="${nextRow}"][data-col="${colName}"]`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  return (
    <div className="space-y-4" data-testid="create-cells-page">
      {/* Configuration */}
      <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base text-white flex items-center gap-2">
            <Zap className="w-4 h-4 text-neon-blue" />
            Configuration du lot
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Nombre de cellules</Label>
            <div className="grid grid-cols-5 gap-2" data-testid="cell-count-buttons">
              {CELL_COUNTS.map((n) => (
                <Button
                  key={n}
                  variant={count === n && !isCustomMode ? "default" : "outline"}
                  onClick={() => { setCount(n); setIsCustomMode(false); }}
                  className={`font-mono text-lg h-14 rounded-lg ${
                    count === n && !isCustomMode ? "bg-gradient-to-r from-neon-blue to-neon-purple text-white neon-glow-blue" : "border-neon-blue/20 hover:border-neon-blue hover:text-neon-blue bg-transparent"
                  }`}
                  data-testid={`cell-count-${n}`}
                >
                  {n}
                </Button>
              ))}
              <Button
                variant={isCustomMode ? "default" : "outline"}
                onClick={() => setShowCustomModal(true)}
                className={`font-mono text-base h-14 rounded-lg ${
                  isCustomMode ? "bg-gradient-to-r from-neon-blue to-neon-purple text-white neon-glow-blue" : "border-neon-blue/20 hover:border-neon-blue hover:text-neon-blue bg-transparent"
                }`}
                data-testid="cell-count-custom"
              >
                <MoreHorizontal className="w-5 h-5 mr-1" />
                Autres
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Prefixe / Serie</Label>
              <Input
                value={prefix}
                onChange={(e) => setPrefix(e.target.value.slice(0, 3))}
                placeholder="EY"
                maxLength={3}
                className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-lg h-12 uppercase rounded-lg"
                data-testid="cell-prefix-input"
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={generateCells}
                disabled={!prefix.trim() || !count}
                className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold uppercase tracking-wide hover:from-neon-blue-hover hover:to-neon-purple rounded-lg h-12 w-full sm:w-auto px-6 neon-glow-blue"
                data-testid="generate-cells-button"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Generer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cells Table */}
      {cells.length > 0 && (
        <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl" data-testid="cells-table-card">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="font-heading text-base text-white">
                {cells.length} cellules generees
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addCycle("decharge")}
                  className="border-neon-yellow/30 text-neon-yellow hover:bg-neon-yellow-muted rounded-lg h-9 text-xs bg-transparent"
                  data-testid="add-discharge-button"
                >
                  <BatteryCharging className="w-3 h-3 mr-1.5" />
                  + Ajouter decharge
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => addCycle("recharge")}
                  className="border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan-muted rounded-lg h-9 text-xs bg-transparent"
                  data-testid="add-recharge-button"
                >
                  <RotateCcw className="w-3 h-3 mr-1.5" />
                  + Ajouter recharge
                </Button>
                <Button
                  onClick={saveBatch}
                  disabled={saving}
                  className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold uppercase tracking-wide hover:from-neon-blue-hover hover:to-neon-purple rounded-lg h-9 neon-glow-blue"
                  data-testid="save-batch-button"
                >
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Enregistrer le lot</>}
                </Button>
              </div>
            </div>
            {cycles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {cycles.map((c, i) => (
                  <Badge key={i} variant="outline" className={`font-mono text-[10px] ${c.type === "decharge" ? "border-neon-yellow/30 text-neon-yellow" : "border-neon-cyan/30 text-neon-cyan"}`}>
                    {c.type === "decharge" ? "Decharge" : "Recharge"} {c.index}
                  </Badge>
                ))}
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-neon-blue/10 hover:bg-transparent">
                    <TableHead className="bg-cyber-elevated text-[10px] font-mono uppercase tracking-wider text-muted-foreground sticky left-0 z-10 bg-cyber-elevated min-w-[70px]">N cell.</TableHead>
                    <TableHead className="bg-cyber-elevated text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Cap. (Ah)</TableHead>
                    <TableHead className="bg-cyber-elevated text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Res. (mohm)</TableHead>
                    <TableHead className="bg-cyber-elevated text-[10px] font-mono uppercase tracking-wider text-muted-foreground">T. dech. (min)</TableHead>
                    {cycles.map((c, ci) => (
                      <TableHead key={`start-${ci}`} colSpan={2} className={`bg-cyber-elevated text-[10px] font-mono uppercase tracking-wider text-center ${c.type === "decharge" ? "text-neon-yellow" : "text-neon-cyan"}`}>
                        {cycleLabel(c)} (V deb / V fin)
                      </TableHead>
                    ))}
                    <TableHead className="bg-cyber-elevated text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Etat</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cells.map((cell, i) => (
                    <TableRow key={i} className="border-neon-blue/5 hover:bg-cyber-highlight transition-colors">
                      <TableCell className="font-mono text-sm text-neon-blue font-bold sticky left-0 bg-cyber-surface z-10" data-testid={`cell-number-${i}`}>
                        {isCustomMode ? (
                          <Input
                            value={cell.cell_number}
                            onChange={(e) => updateCell(i, "cell_number", e.target.value.toUpperCase())}
                            placeholder={`EY${i + 1}`}
                            className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-xs h-10 w-24 text-neon-blue font-bold rounded-lg"
                            data-testid={`cell-number-input-${i}`}
                          />
                        ) : (
                          cell.cell_number
                        )}
                      </TableCell>
                      <TableCell>
                        <NumericInput step="1" value={cell.capacity_ah || ""} onChange={(v) => updateCell(i, "capacity_ah", v)} placeholder="6500" className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-xs h-10 w-20 rounded-lg" data-testid={`cell-capacity-${i}`} />
                      </TableCell>
                      <TableCell>
                        <NumericInput step="0.1" value={cell.internal_resistance || ""} onChange={(v) => updateCell(i, "internal_resistance", v)} placeholder="15.0" className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-xs h-10 w-20 rounded-lg" data-testid={`cell-resistance-${i}`} />
                      </TableCell>
                      <TableCell>
                        <NumericInput step="1" value={cell.discharge_time || ""} onChange={(v) => updateCell(i, "discharge_time", v)} placeholder="45" className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-xs h-10 w-20 rounded-lg" data-testid={`cell-discharge-${i}`} />
                      </TableCell>
                      {(cell.cycles || []).map((cy, ci) => (
                        <TableCell key={`vs-${ci}`} colSpan={2}>
                          <div className="flex gap-1">
                            <NumericInput step="0.01" value={cy.voltage_start || ""} onChange={(v) => updateCycleValue(i, ci, "voltage_start", v)} onKeyDown={(e) => handleNextFocus(e, i, `vs${ci}`)} data-row={i} data-col={`vs${ci}`} placeholder="V deb" className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-xs h-10 w-16 rounded-lg" data-testid={`cell-cycle-vs-${i}-${ci}`} />
                            <NumericInput step="0.01" value={cy.voltage_end || ""} onChange={(v) => updateCycleValue(i, ci, "voltage_end", v)} onKeyDown={(e) => handleNextFocus(e, i, `ve${ci}`)} data-row={i} data-col={`ve${ci}`} placeholder="V fin" className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-xs h-10 w-16 rounded-lg" data-testid={`cell-cycle-ve-${i}-${ci}`} />
                          </div>
                        </TableCell>
                      ))}
                      <TableCell>
                        <Select value={cell.state} onValueChange={(v) => updateCell(i, "state", v)}>
                          <SelectTrigger className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-xs h-10 w-28 rounded-lg" data-testid={`cell-state-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Neuve">Neuve</SelectItem>
                            <SelectItem value="Tres bon">Tres bon</SelectItem>
                            <SelectItem value="Bon">Bon</SelectItem>
                            <SelectItem value="A surveiller">A surveiller</SelectItem>
                            <SelectItem value="Defectueuse">Defectueuse</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>

          <div className="p-4 border-t border-neon-blue/10 sm:hidden">
            <Button
              onClick={saveBatch}
              disabled={saving}
              className="w-full bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold uppercase tracking-wide hover:from-neon-blue-hover hover:to-neon-purple rounded-lg h-12 text-base neon-glow-blue"
              data-testid="save-batch-button-mobile"
            >
              <Save className="w-5 h-5 mr-2" />
              Enregistrer le lot
            </Button>
          </div>
        </Card>
      )}

      {/* Modal custom count */}
      <Dialog open={showCustomModal} onOpenChange={setShowCustomModal}>
        <DialogContent className="bg-cyber-surface border-neon-blue/20 rounded-xl sm:max-w-md" data-testid="custom-count-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <MoreHorizontal className="w-5 h-5 text-neon-blue" />
              Taille de lot personnalisee
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Combien de cellules souhaitez-vous creer ?
              </Label>
              <NumericInput
                autoFocus
                value={customCount}
                onChange={(v) => setCustomCount(v.replace(/[^0-9]/g, ""))}
                placeholder="Ex: 12, 25, 36..."
                className="bg-cyber-bg/50 border-neon-blue/20 font-mono text-lg h-12 rounded-lg"
                onKeyDown={(e) => { if (e.key === "Enter") generateCustomCells(); }}
                data-testid="custom-count-input"
              />
              <p className="text-[11px] font-mono text-muted-foreground">
                Entre 1 et 500 cellules. Vous saisirez les N cellules manuellement.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => { setShowCustomModal(false); setCustomCount(""); }}
              className="rounded-lg border-neon-blue/20 bg-transparent"
              data-testid="custom-count-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={generateCustomCells}
              disabled={!customCount || parseInt(customCount, 10) < 1}
              className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold uppercase tracking-wide rounded-lg neon-glow-blue"
              data-testid="custom-count-generate"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Generer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
